import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  People as PeopleIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cardHolderAPI } from "../../services/cardHolderAPI";
import { nfcCardAPI, NFCCard } from "../../services/nfcCardAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import HolderAvatar from "../../components/layout/HolderAvatar";
import SortableTableCell from "../../components/layout/SortableTableCell";
import TableActionButton from "../../components/layout/TableActionButton";
import { useTableSort } from "../../hooks/useTableSort";
import NfcCardScanInput from "../../components/nfc/NfcCardScanInput";
import {
  exportNfcCardsExcel,
  exportNfcCardsPdf,
  fetchAllNfcCards,
} from "../../utils/nfcCardExport";
import { tokens } from "../../theme";

/** Compact header actions — aligned with export icon buttons */
const headerActionBtnSx = {
  minHeight: 32,
  py: 0.375,
  px: 1.25,
  fontSize: "0.8125rem",
  lineHeight: 1.2,
  "& .MuiButton-startIcon": { mr: 0.5 },
  "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 18 },
} as const;

const headerExportIconBtnSx = {
  width: 32,
  height: 32,
  p: 0.5,
  border: "1px solid",
  borderColor: "divider",
} as const;

type NfcCardSortField =
  | "cardNumber"
  | "balance"
  | "holder"
  | "status"
  | "isAssigned"
  | "createdAt";

type StatusFilter = "all" | NFCCard["status"];
type AssignedFilter = "all" | "assigned" | "unassigned";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const uiCardNumber = (card?: NFCCard | null) => card?.displayNumber || "";

const NFCCardsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const holderFilter = searchParams.get("holderId");
  const { user } = useAuthStore();
  const { canRead, canPerformRecharge, hasAnyRole } = useRBAC();
  const isSuperAdmin = hasAnyRole("SUPER_ADMIN");
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<NfcCardSortField>("cardNumber", "asc");
  const onSort = (field: NfcCardSortField) => handleSort(field, () => setPage(0));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") || "").trim()
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState<NFCCard | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; card: NFCCard | null }>({
    open: false,
    card: null,
  });
  const [newCardNumber, setNewCardNumber] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedHolderId, setSelectedHolderId] = useState("");
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);

  const buildExportFilters = () => ({
    ...sortParams,
    branchId: user?.branchId,
    holderId: holderFilter || undefined,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(assignedFilter === "assigned" ? { isAssigned: true } : {}),
    ...(assignedFilter === "unassigned" ? { isAssigned: false } : {}),
  });

  const handleExportPdf = async () => {
    setExporting("pdf");
    setAlert(null);
    try {
      const cards = await fetchAllNfcCards(buildExportFilters());
      if (!cards.length) {
        setAlert({ type: "error", message: "No NFC cards to export." });
        return;
      }
      exportNfcCardsPdf(cards);
      setAlert({
        type: "success",
        message: `Exported ${cards.length} NFC card(s) as PDF.`,
      });
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message || err.message || "PDF export failed",
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    setExporting("excel");
    setAlert(null);
    try {
      const cards = await fetchAllNfcCards(buildExportFilters());
      if (!cards.length) {
        setAlert({ type: "error", message: "No NFC cards to export." });
        return;
      }
      exportNfcCardsExcel(cards);
      setAlert({
        type: "success",
        message: `Exported ${cards.length} NFC card(s) as Excel (CSV).`,
      });
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message || err.message || "Excel export failed",
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: holdersData } = useQuery({
    queryKey: ["cardHoldersAll"],
    queryFn: () => cardHolderAPI.getCardHolders({ pageNo: 1, pageSize: 200 }),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "nfcCards",
      page,
      rowsPerPage,
      debouncedSearch,
      statusFilter,
      assignedFilter,
      holderFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      if (holderFilter) {
        const result = await nfcCardAPI.getCardsByHolder(holderFilter);
        return {
          cards: result.cards || [],
          total: result.totalCards || 0,
        };
      }
      return nfcCardAPI.getCards({
        page: page + 1,
        limit: rowsPerPage,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(assignedFilter === "assigned" ? { isAssigned: true } : {}),
        ...(assignedFilter === "unassigned" ? { isAssigned: false } : {}),
        branchId: user?.branchId,
      });
    },
  });

  const cards = useMemo(() => {
    const list = data?.cards || [];
    if (!holderFilter || !debouncedSearch) return list;
    const q = debouncedSearch.toLowerCase();
    return list.filter(
      (c: NFCCard) =>
        uiCardNumber(c).toLowerCase().includes(q) ||
        (c.holder?.holderName || "").toLowerCase().includes(q) ||
        (c.holder?.phone || "").includes(debouncedSearch)
    );
  }, [data, holderFilter, debouncedSearch]);

  const createInFlightRef = useRef(false);
  const createSucceededRef = useRef(false);

  const closeCreateDialog = useCallback(() => {
    setCreateError(null);
    setNewCardNumber("");
    setCreateOpen(false);
  }, []);

  const openCreateDialog = useCallback(() => {
    createInFlightRef.current = false;
    createSucceededRef.current = false;
    setNewCardNumber("");
    setCreateError(null);
    setCreateOpen(true);
  }, []);

  const { mutate: createCard, isPending: isCreating } = useMutation({
    mutationFn: (cardNumber: string) =>
      nfcCardAPI.registerInventoryCard(cardNumber, user?.branchId || ""),
    onSuccess: (card) => {
      createSucceededRef.current = true;
      queryClient.invalidateQueries({ queryKey: ["nfcCards"] });
      setAlert({
        type: "success",
        message: `Card ${uiCardNumber(card)} registered (unassigned, ₹0 balance)`,
      });
      closeCreateDialog();
    },
    onError: (err: any) => {
      if (createSucceededRef.current) return;

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to create card";
      const text = /already exists/i.test(String(msg))
        ? "This card is already registered in inventory. Use NFC Cards list to find it."
        : String(msg);

      setCreateError(text);
    },
    onSettled: () => {
      createInFlightRef.current = false;
    },
  });

  const submitCreateCard = useCallback(
    (cardNumber: string) => {
      const trimmed = cardNumber.trim();
      if (!trimmed || createInFlightRef.current) return;
      if (!user?.branchId) {
        setCreateError("Your user has no branch — cannot create card.");
        return;
      }
      setCreateError(null);
      createInFlightRef.current = true;
      createCard(trimmed);
    },
    [user?.branchId, createCard]
  );

  const handleScanAndAdd = useCallback(
    (cardNumber: string) => {
      setNewCardNumber(cardNumber);
      setCreateError(null);
      submitCreateCard(cardNumber);
    },
    [submitCreateCard]
  );

  const { mutate: assignCard } = useMutation({
    mutationFn: ({ cardId, holderId }: { cardId: string; holderId: string }) =>
      nfcCardAPI.assignCard(cardId, holderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nfcCards"] });
      setAlert({ type: "success", message: "Card assigned successfully" });
      setAssignOpen(null);
      setSelectedHolderId("");
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to assign card",
      });
    },
  });

  const { mutate: unassignCard } = useMutation({
    mutationFn: (cardId: string) => nfcCardAPI.unassignCard(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nfcCards"] });
      setAlert({ type: "success", message: "Card unassigned" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to unassign card",
      });
    },
  });

  const { mutate: deleteCard } = useMutation({
    mutationFn: (cardId: string) => nfcCardAPI.deleteCard(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nfcCards"] });
      setAlert({ type: "success", message: "Card deleted" });
      setDeleteDialog({ open: false, card: null });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete card",
      });
    },
  });

  const statusChip = (status: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      ACTIVE: { bg: tokens.colors.success.light, color: tokens.colors.success.main },
      INACTIVE: { bg: tokens.colors.neutral[100], color: tokens.colors.neutral[600] },
      BLOCKED: { bg: alpha(tokens.colors.error.main, 0.12), color: tokens.colors.error.main },
      LOST: { bg: alpha(tokens.colors.warning.main, 0.12), color: tokens.colors.warning.main },
    };
    const s = styles[status] || styles.INACTIVE;
    return (
      <Chip
        label={status}
        size="small"
        sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: s.bg, color: s.color, border: "none" }}
      />
    );
  };

  const assignedChip = (assigned: boolean) => (
    <Chip
      label={assigned ? "Assigned" : "Unassigned"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        bgcolor: assigned ? alpha(tokens.colors.dashBlue, 0.12) : alpha(tokens.colors.dashOrange, 0.12),
        color: assigned ? tokens.colors.dashBlue : tokens.colors.dashOrange,
        border: "none",
      }}
    />
  );

  const filterDropdowns = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }} disabled={!!holderFilter}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
          <MenuItem value="BLOCKED">Blocked</MenuItem>
          <MenuItem value="LOST">Lost</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }} disabled={!!holderFilter}>
        <InputLabel>Assignment</InputLabel>
        <Select
          value={assignedFilter}
          label="Assignment"
          onChange={(e) => {
            setAssignedFilter(e.target.value as AssignedFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Cards</MenuItem>
          <MenuItem value="assigned">Assigned</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  const clearHolderFilter = () => {
    searchParams.delete("holderId");
    setSearchParams(searchParams);
    setPage(0);
  };

  return (
    <Box>
      <PageHeader
        title="NFC Cards"
        subtitle="Register physical cards and link them to card holders"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "NFC Cards" },
        ]}
        action={
          <>
            {isSuperAdmin && (
              <>
                <Tooltip title="Export PDF (all matching filters)">
                  <span>
                    <IconButton
                      size="small"
                      sx={headerExportIconBtnSx}
                      onClick={handleExportPdf}
                      disabled={Boolean(exporting)}
                    >
                      <PdfIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Export Excel — CSV for Excel (all matching filters)">
                  <span>
                    <IconButton
                      size="small"
                      sx={headerExportIconBtnSx}
                      onClick={handleExportExcel}
                      disabled={Boolean(exporting)}
                    >
                      <ExcelIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {canRead("nfc_cards") && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate("/cardholders")}
                sx={{ ...headerActionBtnSx, borderColor: "divider", color: "text.primary" }}
              >
                Card Holders
              </Button>
            )}
            {canPerformRecharge() && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PaymentIcon />}
                onClick={() => navigate("/quick-recharge")}
                sx={{ ...headerActionBtnSx, borderColor: "divider", color: "text.primary" }}
              >
                Quick Recharge
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={headerActionBtnSx}
            >
              Add Card
            </Button>
          </>
        }
      />

      {holderFilter && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<CloseIcon />} onClick={clearHolderFilter}>
              Show all cards
            </Button>
          }
        >
          Showing cards for selected holder only.
        </Alert>
      )}

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading cards: {(error as Error).message}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search card number, holder name, mobile…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          !holderFilter ? (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={data?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ width: "100%" }}
            />
          ) : undefined
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading NFC cards…</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={72}>S.No.</TableCell>
                  <SortableTableCell label="Card" field="cardNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Balance" field="balance" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Holder" field="holder" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Assignment" field="isAssigned" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cards.map((card: NFCCard, index: number) => (
                  <TableRow key={card.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: tokens.radius.sm,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(tokens.colors.dashOrange, 0.12),
                            color: tokens.colors.dashOrange,
                          }}
                        >
                          <CreditCardIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {uiCardNumber(card)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Physical NFC card
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={Number(card.balance) < 500 ? "error.main" : "text.primary"}
                      >
                        {formatCurrency(card.balance)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {card.holder?.holderName ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/cardholders/${card.holder!.id}`)}
                        >
                          <HolderAvatar name={card.holder.holderName} size={32} />
                          <Box minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {card.holder.holderName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {card.holder.phone || "—"}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{statusChip(card.status)}</TableCell>
                    <TableCell>{assignedChip(card.isAssigned)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canPerformRecharge() && (
                          <TableActionButton
                            title="Quick recharge"
                            variant="success"
                            onClick={() =>
                              navigate(`/quick-recharge?displayNumber=${encodeURIComponent(card.displayNumber)}`)
                            }
                          >
                            <PaymentIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {!card.isAssigned ? (
                          <TableActionButton
                            title="Assign to holder"
                            variant="primary"
                            onClick={() => setAssignOpen(card)}
                          >
                            <LinkIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        ) : (
                          <TableActionButton
                            title="Unassign card"
                            variant="warning"
                            onClick={() => unassignCard(card.id)}
                          >
                            <LinkOffIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {!card.isAssigned && (
                          <TableActionButton
                            title="Delete card"
                            variant="danger"
                            onClick={() => setDeleteDialog({ open: true, card })}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {cards.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No NFC cards found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog
        open={createOpen}
        onClose={() => {
          if (!isCreating) closeCreateDialog();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isCreating}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Register NFC card</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Card is saved with no holder and ₹0 balance. Assign it later from a holder profile.
          </Typography>
          <NfcCardScanInput
            value={newCardNumber}
            onChange={(v) => {
              setNewCardNumber(v);
              if (createError) setCreateError(null);
            }}
            onScanComplete={handleScanAndAdd}
            hideScannedValue
            disabled={isCreating}
            hint="Tap card — registers as unassigned inventory card"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreateDialog} disabled={isCreating} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!newCardNumber.trim() || isCreating}
            onClick={() => submitCreateCard(newCardNumber)}
          >
            {isCreating ? <CircularProgress size={22} color="inherit" /> : "Add Card"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!assignOpen} onClose={() => setAssignOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign card to holder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Card: <strong>{uiCardNumber(assignOpen)}</strong>
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Card Holder</InputLabel>
            <Select
              value={selectedHolderId}
              label="Card Holder"
              onChange={(e) => setSelectedHolderId(e.target.value)}
            >
              {(holdersData?.items || []).map((holder: { id: string; holderName: string; phone: string }) => (
                <MenuItem key={holder.id} value={holder.id}>
                  {holder.holderName} ({holder.phone})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignOpen(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedHolderId || !assignOpen}
            onClick={() =>
              assignOpen && assignCard({ cardId: assignOpen.id, holderId: selectedHolderId })
            }
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, card: null })}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete card</DialogTitle>
        <DialogContent>
          <Typography>
            Delete card <strong>{uiCardNumber(deleteDialog.card)}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, card: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteDialog.card && deleteCard(deleteDialog.card.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NFCCardsPage;
